import classNames from 'classnames';

import React, { Component } from 'react';

import Icon, { ICON_TYPES } from '@commons/Icon';
import Question from '@commons/Question';

import FileRow from './FileRow';
import styles from './fileupload.scss';
import { isSameFiles } from './helpers/file';

interface FileUploadProps {
  acceptMultipleFiles: boolean;
  fields?: {
    [key: string]: string;
  };
  fileMaxError?: {
    error?: number;
    errorMessage?: string;
  };
  files?: any[];
  isMobile: boolean;
  onClearMaxError: (...args: unknown[]) => unknown;
  onFileDelete: (...args: unknown[]) => unknown;
  onFileError: (...args: unknown[]) => unknown;
  onFileUpload: (...args: unknown[]) => unknown;
  onUploadButtonClick: (...args: unknown[]) => unknown;
  questionId: string;
  reachedMaxUpload: boolean;
  requiredField?: string;
  showUploadIcon: boolean;
  validationEmpty?: string;
  validationFailed?: string;
  validationMessages?: string[];
  // labels
  actionButtonLabel?: string;
  consentLabel?: string;
  fileFormatLabel?: string;
  inputAcceptExtensionsLabel?: string;
  maxFileSizeLabel?: string;
  maxFilesLabel?: string;
  prefixActionButtonLabel?: string;
  questionLabel?: string;
}

export default class FileUpload extends Component<FileUploadProps> {
  constructor(props) {
    super(props);

    this.handleDroppedFiles = this.handleDroppedFiles.bind(this);
    this.handleUploadButton = this.handleUploadButton.bind(this);
    this.setComponentContainerRef = this.setComponentContainerRef.bind(this);
    this.setInputRef = this.setInputRef.bind(this);
    this.fileUploadContainerRef = React.createRef(null);
  }

  componentDidMount() {
    document.addEventListener('drop', this.handleDroppedFiles);
  }

  shouldComponentUpdate(nextProps) {
    const {
      actionButtonLabel: nextActionButtonLabel,
      files: nextFiles,
      fileMaxError: nextFileMaxError
    } = nextProps;
    const { actionButtonLabel, files, fileMaxError } = this.props;
    return (
      !isSameFiles(files, nextFiles) ||
      fileMaxError !== nextFileMaxError ||
      actionButtonLabel !== nextActionButtonLabel
    );
  }

  componentWillUnmount() {
    document.removeEventListener('drop', this.handleDroppedFiles);
  }

  setInputRef($uploadInput) {
    this.uploadInputRef = $uploadInput;
  }

  setComponentContainerRef($divContainer) {
    this.containerRef = $divContainer;
  }

  handleDroppedFiles(e) {
    e.preventDefault();
    const isDroppedOn = e.target === this.containerRef || this.containerRef.contains(e.target);
    if (!isDroppedOn) return;

    const { files } = e.dataTransfer;
    if (!files.length) return;

    this.uploadInputRef.value = '';
    // Passed container reference to `handleFileUpload` to shift focus to first focusable element inside uploader, preventing JAWS focus from moving to the page's first element.
    this.props.onFileUpload({ target: { files } }, this.fileUploadContainerRef.current);
  }

  handleUploadButton(e) {
    e.stopPropagation();
    e.preventDefault();
    this.uploadInputRef.value = '';
    this.props.onUploadButtonClick(e);
    this.uploadInputRef.click();
  }

  render() {
    const {
      acceptMultipleFiles,
      fields,
      fileMaxError,
      files,
      isMobile,
      onClearMaxError,
      onFileDelete,
      onFileError,
      onFileUpload,
      questionId,
      reachedMaxUpload,
      requiredField,
      showUploadIcon,
      validationEmpty,
      validationFailed,
      validationMessages,

      actionButtonLabel,
      consentLabel,
      fileFormatLabel,
      inputAcceptExtensionsLabel,
      maxFileSizeLabel,
      maxFilesLabel,
      prefixActionButtonLabel,
      questionLabel
    } = this.props;

    const disabledButtonStyle = { [styles.disabledButton]: reachedMaxUpload };

    return (
      <div ref={this.setComponentContainerRef}>
        <Question
          caption={questionLabel}
          requiredField={requiredField}
          validationEmpty={validationEmpty}
          validationFailed={validationFailed}
          validationMessages={validationMessages}
        />
        <div ref={this.fileUploadContainerRef} className={styles.uploadFormContainer}>
          <ul className={styles.fileListContainer}>
            {files.map((file) => (
              <FileRow
                key={`${file.filename || ''}${file.order}`}
                fields={fields}
                file={file}
                onFileDelete={(file) => {
                  onFileDelete(file, this.fileUploadContainerRef.current);
                }}
                onFileError={(file) => {
                  onFileError(file, this.fileUploadContainerRef.current);
                }}
              />
            ))}
            {fileMaxError && (
              <FileRow
                key={`${questionId}max-error`}
                file={fileMaxError}
                onFileDelete={(file) => {
                  onFileDelete(file, this.fileUploadContainerRef.current);
                }}
                onFileError={() => {
                  onClearMaxError(this.fileUploadContainerRef.current);
                }}
              />
            )}
          </ul>
          {showUploadIcon && (
            <Icon type={ICON_TYPES.ARROW_UP} containerStyles={styles.uploadIcon} />
          )}
          {prefixActionButtonLabel ? (
            <p className={styles.actionText}>
              {prefixActionButtonLabel}
              <button
                className={classNames(styles.inlineBrowseFilesButton, disabledButtonStyle)}
                disabled={reachedMaxUpload}
                onClick={this.handleUploadButton}>
                {actionButtonLabel}
              </button>
            </p>
          ) : (
            <button
              className={classNames(styles.browseFilesButton, disabledButtonStyle)}
              disabled={reachedMaxUpload}
              onClick={this.handleUploadButton}>
              {actionButtonLabel}
            </button>
          )}
          <p className={styles.metaInfoText}>{`${fileFormatLabel}`}</p>
          {isMobile ? (
            <div>
              <p className={styles.metaInfoText}>{maxFilesLabel}</p>
              <p className={styles.metaInfoText}>{maxFileSizeLabel}</p>
            </div>
          ) : (
            <p className={styles.metaInfoText}>
              {maxFilesLabel} {maxFileSizeLabel}
            </p>
          )}
          <p className={styles.consentText}>{consentLabel}</p>
          <input
            id={`${questionId}-upload-input`}
            ref={this.setInputRef}
            hidden
            type="file"
            multiple={acceptMultipleFiles}
            accept={inputAcceptExtensionsLabel}
            onChange={(event) => {
              onFileUpload(event, this.fileUploadContainerRef.current);
            }} // Passed container reference to `handleFileUpload` to shift focus to first focusable element inside uploader, preventing JAWS focus from moving to the page's first element.
          />
        </div>
      </div>
    );
  }
}

FileUpload.defaultProps = {
  fields: {},
  fileMaxError: null,
  files: [],
  requiredField: '',
  validationEmpty: '',
  validationFailed: '',
  validationMessages: [],

  // labels
  actionButtonLabel: '',
  consentLabel: '',
  fileFormatLabel: '',
  inputAcceptExtensionsLabel: '',
  maxFileSizeLabel: '',
  maxFilesLabel: '',
  prefixActionButtonLabel: '',
  questionLabel: ''
};
